import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleExclamation,
  faCircleXmark,
  faCircleInfo,
  faCircle,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { cn } from '@/lib/cn';

export type CustomerBadgeTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info';

const TONE_CLASSES: Record<CustomerBadgeTone, string> = {
  neutral: 'bg-customer-text/[0.08] text-customer-text/80 ring-customer-text/10',
  primary: 'bg-customer-primary/10 text-customer-primary-text ring-customer-primary/20',
  success: 'bg-customer-success/15 text-customer-success-text ring-customer-success/25',
  warning: 'bg-customer-warning/15 text-customer-warning-text ring-customer-warning/30',
  danger: 'bg-customer-danger/10 text-customer-danger-text ring-customer-danger/25',
  info: 'bg-customer-teal/15 text-customer-teal-text ring-customer-teal/25',
};

/* One icon per tone so a status never relies on colour alone
   (UI_UX_STANDARDS Accessibility: colour is never the sole indicator). */
const TONE_ICON: Record<CustomerBadgeTone, IconDefinition> = {
  neutral: faCircle,
  primary: faClock,
  success: faCircleCheck,
  warning: faCircleExclamation,
  danger: faCircleXmark,
  info: faCircleInfo,
};

interface CustomerStatusBadgeProps {
  label: string;
  tone?: CustomerBadgeTone;
  className?: string;
  /** Show the tone icon (or a custom one) before the label. */
  icon?: boolean | IconDefinition;
  size?: 'sm' | 'md';
  /** Solid fill for the one status that must dominate a screen. */
  emphasis?: 'soft' | 'solid';
}

const SOLID: Record<CustomerBadgeTone, string> = {
  neutral: 'bg-customer-text text-customer-surface',
  primary: 'bg-customer-primary text-customer-action-ink',
  success: 'bg-customer-success text-customer-success-ink',
  warning: 'bg-customer-warning text-customer-warning-ink',
  danger: 'bg-customer-danger text-customer-danger-ink',
  info: 'bg-customer-teal text-customer-teal-ink',
};

/**
 * Domain-agnostic status chip. Callers pass a resolved `label` + semantic
 * `tone`; the chip owns the visual language (pill, ring, optional tone icon,
 * two sizes, soft/solid emphasis) so every status on the portal reads the
 * same way. The default output is unchanged for existing call sites.
 */
export function CustomerStatusBadge({
  label,
  tone = 'neutral',
  className = '',
  icon = false,
  size = 'sm',
  emphasis = 'soft',
}: CustomerStatusBadgeProps) {
  const iconDef = icon === true ? TONE_ICON[tone] : icon || null;
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-full font-medium ring-1 ring-inset',
        size === 'sm' ? 'px-2.5 py-0.5 text-xs' : 'px-3 py-1 text-[13px]',
        emphasis === 'solid' ? `${SOLID[tone]} ring-transparent` : TONE_CLASSES[tone],
        className,
      )}
    >
      {iconDef && (
        <FontAwesomeIcon
          icon={iconDef}
          className={size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5'}
          aria-hidden="true"
        />
      )}
      <span className="truncate">{label}</span>
    </span>
  );
}
